import React, { useState } from 'react';

import type { EditorActions } from '@atlaskit/editor-core/actions';

import { HelpWidget } from '@confluence/editor-help';
import { OnboardingEditorBanner } from '@confluence/onboarding-editor';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset.ts';
import { useCreationFocusedNavLand } from '@confluence/experiment-cfind-4473-creation-focused-nav/entry-points/useCreationFocusedNavLand.ts';

import type { FullPageEditorComponentProps } from './FullPageEditorComponent.tsx';

type EditorProps = Pick<FullPageEditorComponentProps, 'contentId' | 'version' | 'spaceKey'>;

type AdditionalProps = {
	isContentCreationPanelOpen: boolean;
	isShowingOfflineBanner: boolean;
	editorActions: EditorActions;
	onClickOpenContentCreationPanel: () => void;
	handleTitleChange: (newTitle: string) => void;
	isInCompanyHubUnpublishedState?: boolean;
	editorApi: FullPageEditorPresetAPI | undefined | null;
};

type OnboardingHelpContainerProps = EditorProps & AdditionalProps;

export const OnboardingHelpContainer = ({
	contentId,
	version,
	spaceKey,
	editorApi,
	isContentCreationPanelOpen,
	isShowingOfflineBanner,
	editorActions,
	onClickOpenContentCreationPanel,
	handleTitleChange,
}: OnboardingHelpContainerProps): React.JSX.Element => {
	const isLivePage = useIsCurrentPageLive();
	const isInCompanyHub = isCompanyHubSpaceKey(spaceKey);

	const { areBannersHidden } = useCreationFocusedNavLand();

	/**
	 * The OnboardingEditorBanner reports when its queries and logic have fully resolved.
	 *
	 * Since we don't render the OnboardingEditorBanner on live pages, loaded state should always be true for live pages.
	 */
	const [, setIsOnboardingBannerDataLoaded] = useState<boolean>(isLivePage);
	const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

	const openHelpDialog = () => {
		editorApi?.helpDialog?.actions.openHelp();
	};

	return (
		<>
			{!areBannersHidden ? (
				<>
					<HelpWidget
						contentId={contentId}
						openHelpDialog={openHelpDialog}
						isNCSEditor
						isCompanyHub={isCompanyHub}
						isContentCreationPanelOpen={isContentCreationPanelOpen}
						isOpen={isHelpMenuOpen}
						setIsOpen={setIsHelpMenuOpen}
					/>

					{/* In the Company Hub editor we already show a similar banner when a user first enters
					the editor and hasn't published the hub yet,
					therefore there is no need for the OnboardingEditorBanner */}
					{!isLivePage && !isInCompanyHub && (
						<OnboardingEditorBanner
							contentId={contentId}
							isContentCreationPanelOpen={isContentCreationPanelOpen}
							isShowingOfflineBanner={isShowingOfflineBanner}
							version={version}
							spaceKey={spaceKey}
							editorActions={editorActions}
							setIsOnboardingBannerDataLoaded={setIsOnboardingBannerDataLoaded}
							onClickOpenContentCreationPanel={onClickOpenContentCreationPanel}
							handleTitleChange={handleTitleChange}
						/>
					)}
				</>
			) : null}
		</>
	);
};
